// src/pages/inventory-control-center/hooks/useAddItemForm.js
import {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from "react";

// Estado inicial del formulario
const INITIAL_FORM_DATA = {
    code: "",
    name: "",
    description: "",
    category: "",
    unit: "piece",
    cost: "",
    price: "",
    currentStock: 0,
    minimumStock: 0,
    maximumStock: "",
    location: "",
    supplier: "",
    barcode: "",
    batch: "",
    serialNumber: "",
    expiryDate: "",
    isPerishable: false,
    requiresBatchControl: false,
    images: [],
    notes: "",
    files: [],
};

export const useAddItemForm = ({
    isOpen,
    onSave,
    onClose,
    userRole = "admin",
    showToast,
}) => {
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [activeStep, setActiveStep] = useState(1);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newProductId, setNewProductId] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const fileInputRef = useRef(null);

    // Opciones de selects
    const categoryOptions = useMemo(
        () => [
            { value: "electronic-components", label: "🔌 Componentes Electrónicos" },
            { value: "sensors", label: "📡 Sensores" },
            { value: "cables", label: "🔗 Cables y Conectores" },
            { value: "control-modules", label: "💻 Módulos de Control" },
            { value: "diagnostic-tools", label: "🛠️ Herramientas de Diagnóstico" },
            { value: "lighting", label: "💡 Iluminación" },
            { value: "power-systems", label: "⚡ Sistemas de Energía" },
            { value: "perishable", label: "⏰ Productos Perecederos" },
        ],
        []
    );

    const supplierOptions = useMemo(
        () => [
            { value: "bosch", label: "🏭 Bosch México" },
            { value: "continental", label: "🚗 Continental Automotive" },
            { value: "delphi", label: "🔧 Delphi Technologies" },
            { value: "denso", label: "🇯🇵 DENSO México" },
            { value: "valeo", label: "⚙️ Valeo México" },
            { value: "hella", label: "💡 HELLA México" },
            { value: "local-supplier", label: "🏪 Proveedor Local" },
        ],
        []
    );

    const locationOptions = useMemo(
        () => [
            { value: "warehouse-a", label: "🏢 Almacén A - Principal" },
            { value: "warehouse-b", label: "🏬 Almacén B - Secundario" },
            { value: "workshop-1", label: "🔧 Taller 1 - Diagnóstico" },
            { value: "workshop-2", label: "⚡ Taller 2 - Reparación" },
            { value: "office", label: "💼 Oficina - Herramientas" },
            { value: "branch-1", label: "📍 Sucursal Norte" },
            { value: "branch-2", label: "📍 Sucursal Sur" },
        ],
        []
    );

    const unitOptions = useMemo(
        () => [
            { value: "piece", label: "📦 Pieza" },
            { value: "box", label: "📦 Caja" },
            { value: "liter", label: "💧 Litro" },
            { value: "kg", label: "⚖️ Kilogramo" },
            { value: "meter", label: "📏 Metro" },
        ],
        []
    );

    const steps = useMemo(
        () => [
            {
                number: 1,
                title: "Información Básica",
                description: "Datos principales",
            },
            {
                number: 2,
                title: "Stock y Precios",
                description: "Gestión de inventario",
            },
            {
                number: 3,
                title: "Ubicación y Control",
                description: "Logística y controles",
            },
            {
                number: 4,
                title: "Multimedia y Archivos",
                description: "Imágenes y documentos",
            },
        ],
        []
    );

    const resetForm = useCallback(() => {
        setFormData(INITIAL_FORM_DATA);
        setActiveStep(1);
        setImagePreview(null);
        setIsSubmitting(false);
        setNewProductId(null);
        setSaveError(null);
        setSaveSuccess(false);
    }, []);

    // Reset cuando el modal se cierra
    useEffect(() => {
        if (!isOpen) {
            const timer = setTimeout(() => {
                resetForm();
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, resetForm]);

    // Validación por paso
    const isStepValid = useCallback(() => {
        switch (activeStep) {
            case 1:
                return (
                    formData.code?.trim() &&
                    formData.name?.trim() &&
                    Boolean(formData.category)
                );
            case 2:
                return (
                    formData.cost !== "" &&
                    formData.price !== "" &&
                    Number(formData.currentStock) >= 0
                );
            case 3:
                return Boolean(formData.location) && Boolean(formData.supplier);
            case 4:
                return true;
            default:
                return false;
        }
    }, [activeStep, formData]);

    const handleInputChange = useCallback((field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
        if (saveError) {
            setSaveError(null);
        }
    }, [saveError]);

    const handleNumberChange = useCallback((field, value) => {
        if (value === "" || value === null || value === undefined) {
            setFormData((prev) => ({
                ...prev,
                [field]: "",
            }));
            return;
        }

        const numValue = Number(value);
        if (!isNaN(numValue)) {
            const finalValue = numValue < 0 ? 0 : numValue;
            setFormData((prev) => ({
                ...prev,
                [field]: finalValue,
            }));
        }
    }, []);

    const handleImageUpload = useCallback((event) => {
        const file = event?.target?.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setSaveError("Solo se permiten archivos de imagen");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setSaveError("La imagen no debe superar los 5MB");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const url = e?.target?.result;
            setImagePreview(url);
            setFormData((prev) => ({
                ...prev,
                images: [
                    ...prev.images,
                    {
                        url,
                        alt: "Product image",
                        name: file.name,
                        size: (file.size / 1024 / 1024).toFixed(2) + " MB",
                    },
                ],
            }));
        };
        reader.onerror = () => {
            setSaveError("Error al cargar la imagen");
        };
        reader.readAsDataURL(file);
    }, []);

    const handleRemoveImage = useCallback((index) => {
        setFormData((prev) => {
            const newImages = prev.images.filter((_, i) => i !== index);
            if (newImages.length === 0) {
                setImagePreview(null);
            }
            return {
                ...prev,
                images: newImages,
            };
        });
    }, []);

    const handleFileUploaded = useCallback((fileData) => {
        setFormData((prev) => ({
            ...prev,
            files: [...prev.files, fileData._id],
        }));
        // si el backend regresa ID de producto al subir archivo, podrías setear newProductId aquí
    }, []);

    const generateBarcode = useCallback(() => {
        const randomBarcode = Math.floor(
            1000000000000 + Math.random() * 9000000000000
        ).toString();
        handleInputChange("barcode", randomBarcode);
    }, [handleInputChange]);

    const generateSKU = useCallback(() => {
        const category = categoryOptions.find(
            (cat) => cat.value === formData.category
        );
        const categoryPrefix = category
            ? category.value.substring(0, 3).toUpperCase()
            : "GEN";
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        const sku = `${categoryPrefix}-${randomNum}`;
        handleInputChange("code", sku);
    }, [formData.category, categoryOptions, handleInputChange]);

    const calculateMargin = useCallback(() => {
        if (!formData.cost || !formData.price) return 0;
        const cost = parseFloat(formData.cost);
        const price = parseFloat(formData.price);
        if (!cost || cost === 0) return 0;
        return ((price - cost) / cost) * 100;
    }, [formData.cost, formData.price]);

    const nextStep = useCallback(() => {
        setActiveStep((prev) => Math.min(prev + 1, steps.length));
    }, [steps.length]);

    const prevStep = useCallback(() => {
        setActiveStep((prev) => Math.max(prev - 1, 1));
    }, []);

    const handleClose = useCallback(() => {
        if (!isSubmitting) {
            onClose?.();
        }
    }, [isSubmitting, onClose]);

    // Guardar
    const handleSubmit = useCallback(async () => {
        if (!isStepValid()) {
            setSaveError("Por favor complete todos los campos requeridos");
            return;
        }

        setIsSubmitting(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            if (!formData.code?.trim()) {
                throw new Error("El código del producto es requerido");
            }

            if (!formData.name?.trim()) {
                throw new Error("El nombre del producto es requerido");
            }

            if (!formData.category) {
                throw new Error("La categoría es requerida");
            }

            const cost = parseFloat(formData.cost) || 0;
            const price = parseFloat(formData.price) || 0;

            if (price < cost) {
                throw new Error("El precio de venta no puede ser menor al costo");
            }

            const currentStockValue = Number(formData.currentStock) || 0;
            const minimumStockValue = Number(formData.minimumStock) || 0;

            const finalData = {
                code: formData.code.trim(),
                name: formData.name.trim(),
                description: formData.description?.trim() || "",
                category: formData.category,
                unit: formData.unit,
                cost,
                price,
                // 👇 importante: este naming debe matchear tu API (stock / minStock)
                stock: currentStockValue,
                minStock: minimumStockValue,
                maximumStock: formData.maximumStock
                    ? parseInt(formData.maximumStock)
                    : null,
                location: formData.location,
                supplier: formData.supplier,
                barcode: formData.barcode?.trim() || "",
                batch: formData.batch?.trim() || "",
                serialNumber: formData.serialNumber?.trim() || "",
                expiryDate: formData.expiryDate || null,
                isPerishable: formData.isPerishable,
                requiresBatchControl: formData.requiresBatchControl,
                notes: formData.notes?.trim() || "",
                files: formData.files,
                images: formData.images,
                status: "active",
                createdBy: userRole,
                createdAt: new Date().toISOString(),
                lastUpdated: new Date().toISOString(),
            };

            console.log("📤 Enviando datos al servidor:", finalData);

            await onSave(finalData);

            setSaveSuccess(true);
            if (showToast) {
                showToast({
                    title: "Producto guardado",
                    message: "El producto se ha guardado correctamente",
                    type: "success",
                });
            }

            setTimeout(() => {
                onClose?.();
            }, 2000);
        } catch (error) {
            console.error("❌ Error al guardar producto:", error);
            const message = error.message || "Error desconocido al guardar el producto";
            setSaveError(message);
            if (showToast) {
                showToast({
                    title: "Error al guardar",
                    message,
                    type: "error",
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [
        formData,
        userRole,
        onSave,
        onClose,
        isStepValid,
        showToast,
    ]);

    return {
        formData,
        setFormData,
        activeStep,
        setActiveStep,
        imagePreview,
        setImagePreview,
        isSubmitting,
        newProductId,
        setNewProductId,
        saveError,
        saveSuccess,
        fileInputRef,
        categoryOptions,
        supplierOptions,
        locationOptions,
        unitOptions,
        steps,
        handleInputChange,
        handleNumberChange,
        handleImageUpload,
        handleRemoveImage,
        handleFileUploaded,
        handleSubmit,
        handleClose,
        nextStep,
        prevStep,
        generateBarcode,
        generateSKU,
        calculateMargin,
        isStepValid,
    };
};
